import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

export default function EscalationChain() {
  const { API } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [regionFilter, setRegionFilter] = useState('');

  useEffect(() => {
    API.get('/admin/escalation-chain').then(r => { setData(r.data); setLoading(false); });
  }, []);

  if (loading) return <div className="page-body"><p>Loading...</p></div>;

  const stages = {
    immediate: { bg: '#EFF6FF', border: '#3B82F6', dot: '#3B82F6', label: 'Immediate' },
    reminder:  { bg: '#FFFBEB', border: '#F59E0B', dot: '#F59E0B', label: 'Every 8hrs' },
    regional:  { bg: '#FFF7ED', border: '#F97316', dot: '#F97316', label: '48 Hours' },
    executive: { bg: '#FEF2F2', border: '#EF4444', dot: '#EF4444', label: '72 Hours' },
  };

  const allRegions = [...new Set(data.facilities.map(f => f.region_name).filter(Boolean))].sort();

  const filtered = data.facilities.filter(f => {
    const q = search.toLowerCase();
    const matchSearch = !search ||
      f.facility_name?.toLowerCase().includes(q) ||
      (f.building_admins || []).some(a => a.name?.toLowerCase().includes(q)) ||
      (f.regional_managers || []).some(m => m.name?.toLowerCase().includes(q)) ||
      (f.trade_staff || []).some(t => t.name?.toLowerCase().includes(q));
    const matchRegion = !regionFilter || f.region_name === regionFilter;
    return matchSearch && matchRegion;
  });

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2>Escalation Chain</h2>
          <p>Who receives alerts for each facility and at what stage</p>
        </div>
      </div>
      <div className="page-body">
        {/* Timeline overview */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-title" style={{ marginBottom: 16 }}>Alert Timeline</div>
          <div style={{ display: 'flex', gap: 0, position: 'relative' }}>
            <div style={{ position: 'absolute', top: 20, left: '10%', right: '10%', height: 2, background: '#E2E8F0', zIndex: 0 }} />
            {Object.entries(stages).map(([key, s], i) => (
              <div key={key} style={{ flex: 1, textAlign: 'center', position: 'relative', zIndex: 1 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: s.dot, margin: '0 auto 8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: 14, boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}>{i + 1}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: s.dot, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
                <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
                  {key === 'immediate' && 'Building Admin'}
                  {key === 'reminder' && 'Building Admin'}
                  {key === 'regional' && 'Regional Manager'}
                  {key === 'executive' && 'All Executive Staff'}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <input className="form-control" placeholder="Search facility, admin, or manager..." value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1, minWidth: 200 }} />
          <select className="form-control" style={{ width: 180 }} value={regionFilter} onChange={e => setRegionFilter(e.target.value)}>
            <option value="">All Regions</option>
            {allRegions.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>

        {/* Facility cards by region */}
        {allRegions.filter(r => !regionFilter || r === regionFilter).map(region => {
          const facs = filtered.filter(f => f.region_name === region);
          if (!facs.length) return null;
          return (
            <div key={region} style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1E3A5F', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10, paddingBottom: 6, borderBottom: '2px solid #E2E8F0' }}>
                📍 {region}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(340px,1fr))', gap: 12 }}>
                {facs.map(f => (
                  <div key={f.facility_id} className="card" style={{ padding: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-heading)', marginBottom: 12 }}>{f.facility_name}</div>

                    {/* Building Admin */}
                    <div style={{ background: stages.immediate.bg, border: `1px solid ${stages.immediate.border}`, borderRadius: 6, padding: '10px 12px', marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: stages.immediate.dot, textTransform: 'uppercase' }}>Immediate + Every 8hrs</span>
                        <span style={{ fontSize: 10, background: stages.immediate.dot, color: 'white', padding: '2px 6px', borderRadius: 10 }}>Building Admin</span>
                      </div>
                      {f.building_admins && f.building_admins.length > 0 ? f.building_admins.map((a, i) => (
                        <div key={i} style={{ marginBottom: i < f.building_admins.length - 1 ? 6 : 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }}>{a.name}</div>
                          <div style={{ fontSize: 11, color: '#3B82F6', marginTop: 2 }}>{a.email}</div>
                        </div>
                      )) : <div style={{ fontSize: 12, color: '#EF4444', fontStyle: 'italic' }}>⚠️ No building admin assigned</div>}
                    </div>

                    {/* Maintenance / Environmental — parallel notification, not an escalation rung */}
                    <div style={{ background: '#F0FDFA', border: '1px solid #99F6E4', borderRadius: 6, padding: '10px 12px', marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#0D9488', textTransform: 'uppercase' }}>On Issues (Immediate)</span>
                        <span style={{ fontSize: 10, background: '#0D9488', color: 'white', padding: '2px 6px', borderRadius: 10 }}>Maint / Environ</span>
                      </div>
                      {f.trade_staff && f.trade_staff.length > 0 ? f.trade_staff.map((t, i) => (
                        <div key={i} style={{ marginBottom: i < f.trade_staff.length - 1 ? 6 : 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }}>
                            {t.name}
                            <span style={{ fontSize: 10, marginLeft: 6, color: '#0D9488', textTransform: 'capitalize', fontWeight: 600 }}>({t.role})</span>
                          </div>
                          <div style={{ fontSize: 11, color: '#0D9488', marginTop: 2 }}>{t.email}</div>
                        </div>
                      )) : <div style={{ fontSize: 12, color: '#94A3B8', fontStyle: 'italic' }}>No maintenance/environmental coverage</div>}
                    </div>

                    {/* Regional Manager */}
                    <div style={{ background: stages.regional.bg, border: `1px solid ${stages.regional.border}`, borderRadius: 6, padding: '10px 12px', marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: stages.regional.dot, textTransform: 'uppercase' }}>48 Hours</span>
                        <span style={{ fontSize: 10, background: stages.regional.dot, color: 'white', padding: '2px 6px', borderRadius: 10 }}>Regional Manager</span>
                      </div>
                      {f.regional_managers && f.regional_managers.length > 0 ? f.regional_managers.map((m, i) => (
                        <div key={i} style={{ marginBottom: i < f.regional_managers.length - 1 ? 6 : 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }}>{m.name}</div>
                          {m.title && <div style={{ fontSize: 11, color: '#64748B' }}>{m.title}</div>}
                          <div style={{ fontSize: 11, color: '#F97316', marginTop: 2 }}>{m.email}</div>
                        </div>
                      )) : <div style={{ fontSize: 12, color: '#EF4444', fontStyle: 'italic' }}>⚠️ No regional manager assigned</div>}
                    </div>

                    {/* Executive */}
                    <div style={{ background: stages.executive.bg, border: `1px solid ${stages.executive.border}`, borderRadius: 6, padding: '10px 12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: stages.executive.dot, textTransform: 'uppercase' }}>72 Hours</span>
                        <span style={{ fontSize: 10, background: stages.executive.dot, color: 'white', padding: '2px 6px', borderRadius: 10 }}>Executive Staff</span>
                      </div>
                      {data.executiveStaff.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                          {data.executiveStaff.map((e, i) => (
                            <div key={i} style={{ fontSize: 11, background: 'white', border: '1px solid #FECACA', borderRadius: 4, padding: '2px 6px', color: '#1E293B' }}>
                              {e.name} {e.title ? `(${e.title})` : ''}
                            </div>
                          ))}
                        </div>
                      ) : <div style={{ fontSize: 12, color: '#EF4444', fontStyle: 'italic' }}>⚠️ No executive staff assigned</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Executive summary card */}
        <div className="card" style={{ marginTop: 8 }}>
          <div className="card-title" style={{ marginBottom: 12 }}>Executive Staff — 72hr Alert Recipients</div>
          <p style={{ fontSize: 13, color: '#64748B', marginBottom: 12 }}>The following people receive alerts when any facility tour remains unresolved after 72 hours.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 8 }}>
            {data.executiveStaff.map((e, i) => (
              <div key={i} style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6, padding: '10px 12px' }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#1E293B' }}>{e.name}</div>
                {e.title && <div style={{ fontSize: 11, color: '#64748B' }}>{e.title}</div>}
                {e.department && <div style={{ fontSize: 11, color: '#64748B' }}>{e.department}</div>}
                <div style={{ fontSize: 11, color: '#EF4444', marginTop: 4 }}>{e.email}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
